import { fetchVendorsProducts } from '@/apis';

export const VENDORS_PRODUCTS_KEY = 'fbc-vendors-products-data';
export const VENDORS_PRODUCTS_TIMESTAMP_KEY = 'fbc-vendors-products-timestamp';
export const CACHE_EXPIRY_TIME = 2 * 60 * 60 * 1000;

// Create a reactive store structure
let vendorsProductsStore = {
  state: {
    vendors: [],
    products: [],
  },
  errorCode: null,
  update(newState) {
    this.state = newState;
    this.errorCode = null;
  },
  setError(errorCode) {
    this.errorCode = errorCode;
  }
};

export { vendorsProductsStore };

const isValidData = (data) => {
  return data.vendors && data.vendors.length > 0 && data.products && data.products.length > 0;
};

const isCacheExpired = () => {
  try {
    const timestamp = sessionStorage.getItem(VENDORS_PRODUCTS_TIMESTAMP_KEY);
    if (!timestamp) {
      return true;
    }
    const now = Date.now();
    const cachedTime = parseInt(timestamp, 10);
    return now - cachedTime > CACHE_EXPIRY_TIME;
  } catch (error) {
    console.error('VENDORS_PRODUCTS - Check cache expiry error:', error);
    return true;
  }
};

const setCacheData = (data) => {
  try {
    sessionStorage.setItem(VENDORS_PRODUCTS_KEY, JSON.stringify(data));
    sessionStorage.setItem(VENDORS_PRODUCTS_TIMESTAMP_KEY, Date.now().toString());
  } catch (error) {
    console.error('VENDORS_PRODUCTS - Set cache error:', error);
  }
};

/**
 * Get vendors products data
 * @param {boolean} cache - Whether to use cache, default true
 */
export const getVendorsProducts = async (useCacheSessionStorage = true) => {
  if (!useCacheSessionStorage) {
    try {
      const result = await fetchVendorsProducts();
      if (isValidData(result)) {
        setCacheData(result);
        vendorsProductsStore.update(result);
      }

      return result;
    } catch (error) {
      console.error('VENDORS_PRODUCTS - API error:', error);
      vendorsProductsStore.setError(error.response?.status || error.code || 500);
      throw error;
    }
  }

  if (vendorsProductsStore && isValidData(vendorsProductsStore.state) && !isCacheExpired()) {
    return vendorsProductsStore.state;
  }

  try {
    const storedValue = sessionStorage.getItem(VENDORS_PRODUCTS_KEY);
    if (storedValue && !isCacheExpired()) {
      const cachedData = JSON.parse(storedValue);
      if (isValidData(cachedData)) {
        vendorsProductsStore.update(cachedData);
        return cachedData;
      }
    }
  } catch (error) {
    sessionStorage.removeItem(VENDORS_PRODUCTS_KEY);
    sessionStorage.removeItem(VENDORS_PRODUCTS_TIMESTAMP_KEY);
    console.error('VENDORS_PRODUCTS - Cache error:', error);
  }

  try {
    const result = await fetchVendorsProducts();
    
    if (isValidData(result)) {
      setCacheData(result);
      vendorsProductsStore.update(result);
    }
    
    return result;
  } catch (error) {
    console.error('VENDORS_PRODUCTS - API error:', error);
    vendorsProductsStore.setError(error.response?.status || error.code || 500);
    throw error;
  }
};

/**
 * Clear cache
 */
export const clearVendorsProductsCache = () => {
  vendorsProductsStore.update({ vendors: [], products: [] });
  sessionStorage.removeItem(VENDORS_PRODUCTS_KEY);
  sessionStorage.removeItem(VENDORS_PRODUCTS_TIMESTAMP_KEY);
};
